import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Film, Play, ArrowRight, TrendingUp, Clock, Eye } from 'lucide-react';
import MainLayout from '../../components/MainLayout/MainLayout';
import ContentCard from '../../components/Common/ContentCard';
import Carousel from '../../components/Common/Carousel';
import CarouselItem from '../../components/Common/CarouselItem';
import SecureImage from '../../components/Media/SecureImage';
import mediaService from '../../services/mediaService';
import type { UnifiedContentItem } from '../../types/unifiedContent';
import { useMediaDuration, formatDuration } from '../../hooks/useMediaDuration';

const MediaPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'all' | 'cinema' | 'contents'>('all');
  const [mediaContent, setMediaContent] = useState<UnifiedContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load initial data using unified content system
  useEffect(() => {
    const loadMediaContent = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const combinedData = await mediaService.getMediaCombined({
          page: 0,
          page_size: 100,
        });
        
        setMediaContent(combinedData || []);
      } catch (err) {
        console.error('Error loading media content:', err);
        const errorMessage = err instanceof Error ? err.message : String(err);
        if (errorMessage.includes('Authentication') || errorMessage.includes('credentials') || errorMessage.includes('session')) {
          setError('Unable to load media content. Please log in to access this content.');
        } else if (errorMessage.includes('Network') || errorMessage.includes('connection')) {
          setError('Unable to load media content. Please check your internet connection and refresh the page.');
        } else {
          setError(errorMessage || 'Unable to load media content right now. Please refresh the page or try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadMediaContent();
  }, []);

  // Filter content based on active tab
  const getFilteredContent = useCallback(() => {
    if (activeTab === 'cinema') {
      return mediaContent.filter(item => 
        item.contentType === 'CINEMA' || item.contentType === 'CINEMA_SERIES'
      );
    } else if (activeTab === 'contents') {
      return mediaContent.filter(item => 
        item.contentType === 'CONTENT' || item.contentType === 'CONTENT_SERIES'
      );
    }
    return mediaContent; // 'all' tab shows everything
  }, [mediaContent, activeTab]);

  // Smart grouping similar to OriginalsPage
  const getGroupedContent = useCallback(() => {
    const filteredItems = getFilteredContent();
    
    // Sort by newest first
    const sortedItems = [...filteredItems].sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    // If we have very limited content, use a single mixed row
    if (sortedItems.length < 10) {
      return { mixed: sortedItems };
    }

    const filteredGroups: {[key: string]: UnifiedContentItem[]} = {};
    const usedIds = new Set<string>();
    const minItemsPerSection = 3;

    // 1. Featured section
    const featured = sortedItems.filter(item => item.isFeatured).slice(0, 12);
    if (featured.length >= minItemsPerSection) {
      filteredGroups.featured = featured;
      featured.forEach(item => usedIds.add(item.id));
    }

    // 2. Group by content types
    const remainingItems = sortedItems.filter(item => !usedIds.has(item.id));
    
    const itemsByType = remainingItems.reduce((acc, item) => {
      const baseType = item.contentType?.replace('_SERIES', '') || 'CONTENT';
      if (!acc[baseType]) acc[baseType] = [];
      acc[baseType].push(item);
      return acc;
    }, {} as Record<string, UnifiedContentItem[]>);

    // Create sections for each content type if enough items
    Object.entries(itemsByType).forEach(([type, items]) => {
      if (items.length >= minItemsPerSection) {
        const sectionKey = type.toLowerCase();
        filteredGroups[sectionKey] = items.slice(0, 12);
        items.slice(0, 12).forEach(item => usedIds.add(item.id));
      }
    });

    // 3. Trending section
    const trending = sortedItems
      .filter(item => !usedIds.has(item.id) && (item.viewCount || 0) > 0)
      .sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0))
      .slice(0, 12);
    
    if (trending.length >= minItemsPerSection) {
      filteredGroups.trending = trending;
      trending.forEach(item => usedIds.add(item.id));
    }

    // 4. See More section
    const seeMoreItems = sortedItems.filter(item => !usedIds.has(item.id));
    if (seeMoreItems.length > 0) {
      filteredGroups.seeMore = seeMoreItems;
    }

    return filteredGroups;
  }, [getFilteredContent]);

  // Convert UnifiedContentItem to ContentCard props
  const convertToContentCard = (item: UnifiedContentItem) => {
    // Generate tags based on content type and category
    const generateTags = () => {
      const tags = [];
      
      if (item.category) tags.push(item.category);
      
      const baseContentType = item.contentType?.replace('_SERIES', '') || 'CONTENT';
      switch (baseContentType) {
        case 'CINEMA':
          tags.push('HD', 'Drama');
          break;
        case 'CONTENT':
          tags.push('Educational', 'Tutorial');
          break;
        default:
          tags.push('Original');
      }
      
      if (item.type === 'SERIES') tags.push('Series');
      if (item.isFeatured) tags.push('Featured');
      
      return tags.slice(0, 4);
    };

    // Map content types properly
    const mapContentType = (contentType: string): 'cinema' | 'content' => {
      const baseType = contentType?.replace('_SERIES', '') || 'CONTENT';
      return baseType.toLowerCase() === 'cinema' ? 'cinema' : 'content';
    };

    // Determine the correct type based on whether this is a series or content
    const cardType = item.type === 'SERIES' ? 'series' as const : mapContentType(item.contentType || 'CONTENT');

    return {
      id: item.id,
      title: item.title,
      thumbnail: item.thumbnailUrl || '',
      type: cardType,
      tags: generateTags(),
      category: item.category,
      // Add series-related props if it's a series
      ...(item.type === 'SERIES' && {
        totalSeasons: item.totalSeasons,
        totalEpisodes: item.totalEpisodes,
        seriesType: item.contentType as 'CINEMA_SERIES' | 'CONTENT_SERIES' | 'PODCAST_SERIES' | 'ANIMATION_SERIES',
      }),
    };
  };

  const handleTabChange = (tab: 'all' | 'cinema' | 'contents') => {
    setActiveTab(tab);
  };

  const heroMedia = getFilteredContent().find(item => item.isFeatured) || getFilteredContent()[0];
  const heroDuration = useMediaDuration(heroMedia?.mediaUrl, heroMedia?.durationSeconds);

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading media content...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Content</h2>
            <p className="text-gray-600 mb-4">{error || 'Something went wrong while loading media content.'}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  const groupedContent = getGroupedContent();

  return (
    <MainLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-slate-950 via-neutral-900 to-slate-950 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-5xl md:text-6xl font-bold mb-6">
                  <span className="font-display bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
                    Media Hub
                  </span>
                </h1>
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  Discover our collection of cinema and content. From cinematic masterpieces to educational content, 
                  explore originals that inspire and entertain.
                </p>
                
                {/* Quick Links */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/media/cinema"
                    className="group bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-700 hover:to-red-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-2xl hover:shadow-orange-500/30 flex items-center justify-center"
                  >
                    <Film className="mr-2 h-5 w-5" />
                    Explore Cinema
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                  </Link>
                  
                  <Link
                    to="/media/contents"
                    className="group bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 flex items-center justify-center"
                  >
                    <Play className="mr-2 h-5 w-5" />
                    Browse Contents
                  </Link>
                </div>
              </div>
              
              {/* Featured Content */}
              {heroMedia && (
                <div className="relative">
                  <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl">
                    <div className="aspect-[4/5] bg-gradient-to-br from-slate-800 to-slate-900 relative">
                      <SecureImage 
                        src={heroMedia.thumbnailUrl?.trim() ? 
                             heroMedia.thumbnailUrl :
                             `https://picsum.photos/400/600?random=${heroMedia.id || 'hero'}`
                        } 
                        alt={heroMedia.title}
                        className="w-full h-full object-cover"
                      />
                      
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20"></div>
                      
                      <div className="absolute top-4 left-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center">
                        <TrendingUp className="w-4 h-4 mr-1" />
                        {heroMedia.isFeatured ? 'Featured' : 'Trending'}
                      </div>
                      
                      <div className="absolute bottom-6 left-6 right-6 text-white">
                        {heroMedia.category && (
                          <div className={`text-white px-2 py-1 rounded text-xs font-medium mb-2 inline-block ${
                            (heroMedia.contentType?.includes('CINEMA')) ? 'bg-red-500' : 'bg-blue-500'
                          }`}>
                            {heroMedia.category}
                          </div>
                        )}
                        <h3 className="text-2xl font-bold mb-2">{heroMedia.title}</h3>
                        <p className="text-gray-200 text-sm mb-4 line-clamp-2">{heroMedia.description}</p>
                        <div className="flex items-center space-x-4 text-sm">
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {heroDuration ? formatDuration(heroDuration) : heroMedia.type === 'SERIES' ? `${heroMedia.totalEpisodes || 0} episodes` : '0:00'}
                          </div>
                          <div className="flex items-center">
                            <Eye className="w-4 h-4 mr-1" />
                            {((heroMedia.viewCount || 0) / 1000).toFixed(0)}K views
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="py-8">
          {/* Tabs */}
          <div className="px-4 sm:px-6 lg:px-8 mb-8">
            <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 max-w-md">
              {[
                { key: 'all', label: 'All Media' },
                { key: 'cinema', label: 'Cinema' },
                { key: 'contents', label: 'Contents' }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key as 'all' | 'cinema' | 'contents')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                    activeTab === tab.key
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Carousel Content */}
          <div className="space-y-4">
            {Object.entries(groupedContent).map(([type, items]) => {
              if (!items || items.length === 0) return null;
              
              const typeLabels: {[key: string]: string} = {
                mixed: 'Latest Media',
                featured: 'Featured',
                trending: 'Trending',
                seeMore: 'See More',
                cinema: 'Cinema',
                content: 'Content'
              };
              
              return (
                <div key={type} className="mb-6">
                  <Carousel title={typeLabels[type] || type.charAt(0).toUpperCase() + type.slice(1)} id={`media-${type}`}>
                    {items.map((item) => {
                      const contentItem = convertToContentCard(item);
                      return (
                        <CarouselItem key={item.id}>
                          <ContentCard {...contentItem} />
                        </CarouselItem>
                      );
                    })}
                  </Carousel>
                </div>
              );
            })}
          </div>

          {/* Empty State */}
          {Object.keys(groupedContent).length === 0 && (
            <div className="text-center py-12">
              <Film className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No content found</h3>
              <p className="text-gray-600">Try selecting a different tab.</p>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default MediaPage;